import { format, isValid as isValidDate } from 'date-fns';
import { useState } from 'react';

import { SeatType } from '@/types';
import { DatePicker, Grid, TextField, Typography } from '@/ui';

import type { ChangeEvent, FC, KeyboardEvent } from 'react';

interface EditCoachAdminSettingsBottomProps {
  startDate?: string | null;
  terminationDate?: string | null;
  seatTypeRates?: Partial<Record<SeatType, number | null>>;
  onStartDateChange: (value: string | null) => void;
  onTerminationDateChange: (value: string | null) => void;
  onSeatTypeRateChange: (seatType: SeatType, value?: number | null) => void;
}

const EditCoachAdminSettingsBottom: FC<EditCoachAdminSettingsBottomProps> = ({
  startDate,
  terminationDate,
  seatTypeRates,
  onStartDateChange,
  onTerminationDateChange,
  onSeatTypeRateChange,
}) => {
  const [startDateError, setStartDateError] = useState(false);
  const [terminationDateError, setTerminationDateError] = useState(false);

  const getDateValue = (date?: string) => {
    if (!date) return null;
    const [yearStr, monthStr, dayStr] = date.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const onDateChange = (
    handler: (value: string | null) => void,
    date: Date | null,
  ) => {
    if (!date) {
      handler(null);
      return;
    }
    if (isValidDate(date)) {
      handler(format(date, 'yyyy-MM-dd'));
    }
  };

  const onSeatRate = (
    seatType: SeatType,
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) =>
    onSeatTypeRateChange(
      seatType,
      e.target.value === '' ? null : parseFloat(e.target.value || '0'),
    );

  const onRateKeyUp = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!/[0-9.]/u.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <Grid spacing={4} container>
      <Grid className="mt-1 flex flex-col gap-4" xs={12} item>
        <DatePicker
          label="Start date"
          slotProps={{
            actionBar: { actions: ['clear'] },
            textField: {
              error: startDateError,
              helperText: startDateError ? 'Invalid date' : undefined,
            },
          }}
          value={getDateValue(startDate ?? undefined)}
          onChange={(date) => onDateChange(onStartDateChange, date)}
          onError={(reason) => setStartDateError(Boolean(reason))}
        />
        <DatePicker
          label="Termination date"
          slotProps={{
            actionBar: { actions: ['clear'] },
            textField: {
              error: terminationDateError,
              helperText: terminationDateError ? 'Invalid date' : undefined,
            },
          }}
          value={getDateValue(terminationDate ?? undefined)}
          onChange={(date) => onDateChange(onTerminationDateChange, date)}
          onError={(reason) => setTerminationDateError(Boolean(reason))}
        />
        <div className="mt-2 flex flex-col gap-3">
          <Typography variant="subtitle2">Seat rates</Typography>
          {Object.values(SeatType).map((seat) => (
            <TextField
              key={seat}
              label={`Rate - ${seat}`}
              placeholder="0"
              value={
                seatTypeRates?.[seat] === null ||
                seatTypeRates?.[seat] === undefined
                  ? ''
                  : String(seatTypeRates[seat])
              }
              onChange={(e) => onSeatRate(seat, e)}
              onKeyUp={onRateKeyUp}
            />
          ))}
        </div>
      </Grid>
    </Grid>
  );
};

export default EditCoachAdminSettingsBottom;
